import React, { useEffect, useState } from "react";
import zoomSdk from "@zoom/appssdk";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryWithAI from "../../images/attendee_initial.svg";
import AttendeeSummarizingIndicator from "./AttendeeSummarizingIndicator";
import AttendeeSummarizeStopped from "./AttendeeSummarizeStopped";
import LogManager from "../utils/LogManager";
import {AIMS_STATUS} from "../../constants";

const SESSION_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  CONNECT_FAIL: "CONNECT_FAIL",
  NOT_FOUND: "NOT_FOUND",
  ERROR: "ERROR",
}

/**
 * AttendeeHomeView Component: This component ensures attendees are kept informed about the summarization status
 *
 * This component is shown to attendees in a Zoom meeting when AiMS is enabled for the meeting.
 * It checks the RTMS status to inform attendees whether summarization is in progress/stopped.
 *
 * Key Features:
 * - Periodically (every 5 seconds) calls the `/session` backend API to get the current session status.
 * (TODO: update to call sdk api after zoom ready at July)
 *
 *   The session status can be one of:
 *     - ACTIVE: Summarization is in progress → shows `AttendeeSummarizingIndicator`
 *     - INACTIVE: Summarization has ended → shows `AttendeeSummarizeStopped`
 *     - All other states → shows default static info view
 * - For localhost testing, we can mocks session status with a dummy UUID.
 */

const AttendeeHomeView = ({ meetingUUID: initialMeetingUUID, aimsStatus: initialAimsStatus }) => {
  const [meetingUUID, setMeetingUUID] = useState(initialMeetingUUID);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [aimsStatus, setAimsStatus] = useState(initialAimsStatus);

  useEffect(() => {
    LogManager.info("[AttendeeHomeView] The aimsStatus is: ", aimsStatus);

    const fetchMeetingUUID = async () => {
      if (!initialMeetingUUID) {
        const result = await zoomSdk.getMeetingUUID();
        LogManager.info("Fetched meetingUUID from API:", result.meetingUUID);
        setMeetingUUID(result.meetingUUID);
      } else {
        LogManager.info("Meeting UUID passed to AttendeeHomeView:", initialMeetingUUID);
      }
    };

    fetchMeetingUUID();
  }, [initialMeetingUUID]);

  useEffect(() => {
    //localhost testing code
    if (window.location.href === "http://localhost:3000/") {
      //INACTIVE to see stop AIMS view
      setSessionStatus(SESSION_STATUS.INACTIVE);
      setMeetingUUID("testUUID");
      // setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      console.log("[Localhost] Set SESSION_STATUS and setMeetingUUID");
      return;
    }

    if (!meetingUUID) return;

    const intervalId = setInterval(() => {
      getSessionStatus(meetingUUID);
    }, 5000); // every 5 seconds

    // Clean up interval when component unmounts
    return () => clearInterval(intervalId);
  }, [meetingUUID]);

  const getSessionStatus = async (uuid) => {
    console.log("Calling /session API with meetingUUID: " + uuid);
    LogManager.info("Calling /session API with meetingUUID: " + uuid);
    try {
      const response = await fetch("/session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingUuid: uuid,
        }),
      });
      const responseJson = await response.json();
      // sample: {StatusCode: 200, ExecutedVersion: "$LATEST", Payload: "\"{\\\"status\\\":\\\"NOT_FOUND\\\",\\\"meetingUuid\\\":\\\"pMTug/Y3QKKMIhrxq3oEMg==\\\"}\""}
      LogManager.info("The response of /session is:", responseJson);

      //TODO error handling for statusCode
      const statusCode = responseJson.StatusCode;

      const payloadString = responseJson.Payload;
      const payloadJsonString = JSON.parse(payloadString);
      //sample: "{\"status\":\"NOT_FOUND\",\"meetingUuid\":\"rOHSUkn3S6ySsO2PemItXA==\"}"
      console.log("The payload is: ", payloadJsonString);
      const sessionJson = JSON.parse(payloadJsonString);
      const sessionStatus = sessionJson.status;
      console.log("Parsed sessionStatus:", sessionStatus);
      LogManager.info("Parsed sessionStatus:", sessionStatus);
      setSessionStatus(sessionStatus);

    } catch (error) {
      console.error("Error fetching session status:", error);
      LogManager.error("Error fetching session status:", error);
    }
  };

  if (sessionStatus === SESSION_STATUS.ACTIVE) {
    return <AttendeeSummarizingIndicator />;
  }

  if (sessionStatus === SESSION_STATUS.INACTIVE) {
    return <AttendeeSummarizeStopped aimsStatus={aimsStatus} />;
  }

  return (
    <Column alignmentHorizontal="stretch" spacing="none">
      <Column height="72px" />
      <Box width="100%" position="relative">
        <img
          src={summaryWithAI}
          alt="summaryWithAI"
          style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }}
        />
      </Box>
      <Column height="16px" />
      <Column spacing="none">
        <Row spacing="none" spacingInset="none">
          <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
            <Box type="outline" spacingInset="none" minWidth="68px" />
          </Theme>
          <Text fontFamily="amazonEmber" type="h50" alignment="start">
            Summarize meetings with AI
          </Text>
        </Row>
      </Column>
      <Column height="16px" spacing="none" />
      <Column spacing="none" spacingInset="none">
        <Row spacing="none" spacingInset="none">
          <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
            <Box type="outline" spacingInset="none" minWidth="68px" />
          </Theme>
          <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
            <Box type="outline" spacingInset="none" maxWidth="218px">
              <Theme tokens={{ textListSpacingVertical: "30px" }}>
                <Text fontFamily="amazonEmber" type="b200" alignment="start" color="secondary">
                  Meeting organizers can turn on meeting summary.
                </Text>
              </Theme>
            </Box>
          </Theme>
        </Row>
      </Column>
    </Column>
  );
};

export default AttendeeHomeView;
